import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Question tags are backend-meaningful — never translated.
// Option texts are display-only; only tags are sent for scoring.
const QUESTION_TAGS = [
  { id: 1, optionTags: [["tier:entry"],["tier:mid"],["tier:high"],["tier:very-high"],["tier:elite"]] },
  { id: 2, optionTags: [["type:1099"],["type:w2"],["type:hybrid"],["type:business"],["type:real-estate"]] },
  { id: 3, optionTags: [["structure:no-scorp"],["structure:no-scorp"],["structure:scorp"],["structure:partnership"],["structure:ccorp"],["structure:no-scorp"]] },
  {
    id: 4,
    showIf: (answers) => {
      const q3 = answers[3];
      return q3 && q3.tags.some(t => t === "structure:no-scorp");
    },
    optionTags: [["gap:scorp-awareness"],["gap:scorp-awareness"],["gap:scorp-implementation"],["scorp:active"]],
  },
  { id: 5,  optionTags: [[],[" retirement:active"],["retirement:active"],["gap:no-sep-or-solo401k"],["gap:retirement-underutilized"],["gap:retirement-underutilized"]] },
  { id: 6,  optionTags: [["deduction:home-office-strong"],["gap:home-office-unclear"],["deduction:home-office-no"],["gap:home-office-unclear"]] },
  { id: 7,  optionTags: [["deduction:vehicle-strong"],["deduction:vehicle-partial"],["deduction:vehicle-no"],["deduction:vehicle-business-owned"]] },
  { id: 8,  optionTags: [["deduction:self-employed-health"],[],["flag:health-coverage-gap"],["gap:hsa"],[]] },
  { id: 9,  optionTags: [["cpa:none"],["cpa:reactive"],["cpa:moderate"],["cpa:proactive"],["cpa:none"]] },
  { id: 10, optionTags: [["strategy:augusta-qualified"],["gap:augusta-awareness"],["strategy:augusta-no"],["gap:augusta-awareness"]] },
  { id: 11, optionTags: [["deduction:qbi-active"],["gap:qbi"],["gap:qbi"],["gap:qbi"]] },
  { id: 12, optionTags: [["frustration:surprise-bills"],["frustration:overpaying-unknown"],["frustration:cpa-reactive"],["frustration:implementation-gap"],["frustration:no-visibility"]] },
];

// Build full question objects from tag metadata + translations
function useQuestions() {
  const { t } = useTranslation();
  return QUESTION_TAGS.map(qMeta => {
    const base = t(`quiz.questions.q${qMeta.id}`, { returnObjects: true });
    const options = (base.options || []).map((optText, idx) => ({
      text: optText,
      tags: (qMeta.optionTags[idx] || []).filter(Boolean),
    }));
    return { ...qMeta, text: base.text || `Q${qMeta.id}`, options };
  });
}

// ── Score band color ──────────────────────────────────────────────────────────
const bandColor = (score) => {
  if (score >= 80) return { bg: "#f0fdf4", border: "#059669", text: "#065f46", badge: "#059669" };
  if (score >= 60) return { bg: "#fffbeb", border: "#d97706", text: "#92400e", badge: "#d97706" };
  if (score >= 40) return { bg: "#fff7ed", border: "#ea580c", text: "#9a3412", badge: "#ea580c" };
  return { bg: "#fff1f2", border: "#dc2626", text: "#7f1d1d", badge: "#dc2626" };
};

// ── Intro Screen ──────────────────────────────────────────────────────────────
function IntroScreen({ onStart, loading }) {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: "center", maxWidth: 520, margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#059669", letterSpacing: "0.12em",
                    textTransform: "uppercase", marginBottom: 12 }}>
        SolelyTax AI
      </div>
      <h1 style={{ fontSize: "clamp(26px,5vw,38px)", fontWeight: 800, color: "#1c1917",
                   lineHeight: 1.15, marginBottom: 12 }}>
        {t('quiz.intro.title')}
      </h1>
      <p style={{ fontSize: 16, color: "#78716c", marginBottom: 32, lineHeight: 1.6 }}>
        {t('quiz.intro.subtitle')}
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 32, flexWrap: "wrap" }}>
        {[
          [t('quiz.intro.stat_questions'), t('quiz.intro.stat_questions_label')],
          [t('quiz.intro.stat_time'),      t('quiz.intro.stat_time_label')],
          [t('quiz.intro.stat_cost'),      t('quiz.intro.stat_cost_label')],
        ].map(([n, l]) => (
          <div key={l} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#059669" }}>{n}</div>
            <div style={{ fontSize: 12, color: "#78716c", textTransform: "uppercase",
                          letterSpacing: "0.08em" }}>{l}</div>
          </div>
        ))}
      </div>
      <button onClick={onStart} disabled={loading} data-testid="start-quiz-btn"
        style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 10,
                 padding: "16px 40px", fontSize: 17, fontWeight: 700, cursor: "pointer",
                 width: "100%", maxWidth: 320, opacity: loading ? 0.6 : 1 }}>
        {loading ? t('quiz.intro.starting_btn') : t('quiz.intro.start_btn')}
      </button>
      <p style={{ marginTop: 14, fontSize: 12, color: "#a8a29e" }}>
        {t('quiz.intro.no_cc')}
      </p>
    </div>
  );
}

// ── Question Screen ───────────────────────────────────────────────────────────
function QuestionScreen({ question, questionNumber, total, onAnswer, selectedIdx }) {
  const { t } = useTranslation();
  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "24px 20px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#78716c", fontWeight: 600 }}>
            {t('quiz.progress.question_of', { current: questionNumber, total })}
          </span>
          <span style={{ fontSize: 12, color: "#a8a29e" }}>
            {t('quiz.progress.complete', { pct: Math.round((questionNumber / total) * 100) })}
          </span>
        </div>
        <div style={{ height: 6, background: "#e7e5e4", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", background: "#059669", borderRadius: 3,
                        width: `${(questionNumber / total) * 100}%`, transition: "width 0.3s ease" }} />
        </div>
      </div>

      <h2 style={{ fontSize: "clamp(19px,3.5vw,25px)", fontWeight: 700, color: "#1c1917",
                   lineHeight: 1.35, marginBottom: 24 }}>
        {question.text}
      </h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {question.options.map((opt, idx) => (
          <button key={idx} onClick={() => onAnswer(opt, idx)} data-testid={`option-${idx}`}
            style={{
              background: selectedIdx === idx ? "#f0fdf4" : "#fff",
              border: selectedIdx === idx ? "2px solid #059669" : "2px solid #e7e5e4",
              borderRadius: 10, padding: "15px 18px", fontSize: 15, fontWeight: 500,
              color: "#1c1917", cursor: "pointer", textAlign: "left", minHeight: 52,
              transition: "all 0.12s ease", display: "flex", alignItems: "center", gap: 12,
            }}
          >
            <span style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                           display: "flex", alignItems: "center", justifyContent: "center",
                           fontSize: 13, fontWeight: 700,
                           background: selectedIdx === idx ? "#059669" : "#f5f5f4",
                           color: selectedIdx === idx ? "#fff" : "#78716c" }}>
              {String.fromCharCode(65 + idx)}
            </span>
            {opt.text}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Results Screen ────────────────────────────────────────────────────────────
function ResultsScreen({ score, band, gapCount, strategyCount, overpaymentDisplay, ovLow, ovHigh, onPayment, sessionId }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail]         = useState("");
  const colors = bandColor(score);

  const goToCheckout = () => {
    if (!firstName.trim() || !email.trim()) {
      toast.error(t('quiz.errors.name_email_required'));
      return;
    }
    if (sessionId) {
      axios.post(`${API}/quiz/${sessionId}/answers`, { answers: {}, all_tags: [] }).catch(() => {});
    }
    navigate(`/audit/checkout?session_id=${sessionId || ""}&name=${encodeURIComponent(firstName)}&email=${encodeURIComponent(email)}`);
  };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 20px" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#059669", letterSpacing: "0.1em",
                      textTransform: "uppercase", marginBottom: 8 }}>
          {t('quiz.results.your_results')}
        </div>
        <h1 style={{ fontSize: "clamp(22px,4vw,32px)", fontWeight: 800, color: "#1c1917", marginBottom: 6 }}>
          {t('quiz.results.score_title')}
        </h1>

        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center",
                      background: colors.bg, border: `3px solid ${colors.border}`,
                      borderRadius: 16, padding: "24px 48px", margin: "16px 0" }}>
          <div style={{ fontSize: 72, fontWeight: 900, color: colors.badge, lineHeight: 1 }}>{score}</div>
          <div style={{ fontSize: 14, color: colors.text, fontWeight: 600 }}>{t('quiz.results.out_of')}</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, background: colors.border,
                        color: "#fff", padding: "4px 14px", borderRadius: 20 }}>{band}</div>
        </div>

        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12,
                      padding: "20px", marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: "#78716c", marginBottom: 4 }}>
            {t('quiz.results.est_overpayment')}
          </div>
          <div style={{ fontSize: "clamp(26px,5vw,36px)", fontWeight: 900, color: "#ea580c" }}>
            {overpaymentDisplay}
          </div>
        </div>

        <div style={{ fontSize: 18, fontWeight: 700, color: "#1c1917", marginBottom: 4 }}>
          {t('quiz.results.strategies_identified', { count: strategyCount })}
        </div>
        <div style={{ fontSize: 14, color: "#78716c", marginBottom: 24 }}>
          {t('quiz.results.report_subtitle')}
        </div>
      </div>

      <div style={{ background: "#fff", border: "2px solid #e7e5e4", borderRadius: 14, padding: "24px 20px" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#1c1917", marginBottom: 4, textAlign: "center" }}>
          {t('quiz.results.get_report')}
        </div>
        <div style={{ fontSize: 14, color: "#78716c", textAlign: "center", marginBottom: 20 }}>
          {t('quiz.results.report_includes')}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <input value={firstName} onChange={e => setFirstName(e.target.value)}
            placeholder={t('quiz.results.first_name_placeholder')}
            data-testid="quiz-first-name"
            style={{ border: "2px solid #e7e5e4", borderRadius: 8, padding: "12px 14px",
                     fontSize: 15, outline: "none", width: "100%", boxSizing: "border-box" }} />
          <input value={email} onChange={e => setEmail(e.target.value)}
            placeholder={t('quiz.results.email_placeholder')}
            type="email" data-testid="quiz-email"
            style={{ border: "2px solid #e7e5e4", borderRadius: 8, padding: "12px 14px",
                     fontSize: 15, outline: "none", width: "100%", boxSizing: "border-box" }} />
        </div>

        <button onClick={goToCheckout} disabled={!firstName.trim() || !email.trim()}
          data-testid="quiz-payment-btn"
          style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 10,
                   padding: "16px", fontSize: 17, fontWeight: 700, cursor: "pointer",
                   width: "100%", opacity: (!firstName.trim() || !email.trim()) ? 0.5 : 1 }}>
          {t('quiz.results.get_report_btn')}
        </button>
        <div style={{ textAlign: "center", marginTop: 10, fontSize: 12, color: "#a8a29e" }}>
          {t('quiz.results.secure_payment')}
        </div>
      </div>
    </div>
  );
}

// ── Success Screen ────────────────────────────────────────────────────────────
function SuccessScreen({ sessionId, firstName, email, score, ovLow, ovHigh }) {
  const { t } = useTranslation();
  const [status, setStatus]           = useState("generating");
  const [reportReady, setReportReady] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/quiz/${sessionId}/status`);
        if (res.data.status === "report_generated" || res.data.email_sent_at) {
          setStatus("done"); setReportReady(true); clearInterval(poll);
        }
        if (++attempts > 30) clearInterval(poll);
      } catch { clearInterval(poll); }
    }, 2000);
    return () => clearInterval(poll);
  }, [sessionId]);

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "40px 20px", textAlign: "center" }}>
      <div style={{ fontSize: 60, marginBottom: 16 }}>{reportReady ? "✅" : "⏳"}</div>
      <h1 style={{ fontSize: "clamp(22px,4vw,30px)", fontWeight: 800, color: "#1c1917", marginBottom: 12 }}>
        {reportReady ? t('quiz.success.report_ready_title') : t('quiz.success.generating_title')}
      </h1>
      <p style={{ fontSize: 15, color: "#78716c", marginBottom: 24, lineHeight: 1.6 }}>
        {reportReady
          ? t('quiz.success.report_ready_body', { email })
          : t('quiz.success.generating_body')}
      </p>

      {reportReady && (
        <>
          <a href={`${API}/quiz/${sessionId}/download`} target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-block", background: "#059669", color: "#fff",
                     textDecoration: "none", borderRadius: 10, padding: "14px 28px",
                     fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
            {t('quiz.success.download_btn')}
          </a>
          <div style={{ background: "#f0fdf4", border: "1px solid #a7f3d0", borderRadius: 12,
                        padding: 20, marginTop: 16, textAlign: "left" }}>
            <div style={{ fontWeight: 700, color: "#065f46", marginBottom: 6 }}>
              {t('quiz.success.founding_title')}
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#059669", marginBottom: 4 }}>
              {t('quiz.success.founding_price')}
            </div>
            <div style={{ fontSize: 13, color: "#78716c", marginBottom: 12 }}>
              {t('quiz.success.founding_after')}
            </div>
            <a href="/waitlist/founding"
              style={{ display: "inline-block", background: "#059669", color: "#fff",
                       textDecoration: "none", borderRadius: 8, padding: "10px 20px",
                       fontSize: 14, fontWeight: 700 }}>
              {t('quiz.success.founding_btn')}
            </a>
          </div>
        </>
      )}

      {!reportReady && (
        <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: "50%",
                                  background: "#059669",
                                  animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Quiz Funnel ──────────────────────────────────────────────────────────
export default function QuizFunnel() {
  const navigate       = useNavigate();
  const { page }       = useParams();
  const [searchParams] = useSearchParams();
  const { t }          = useTranslation();

  const questions = useQuestions();

  const [sessionId, setSessionId]     = useState(() => localStorage.getItem("quiz_session_id") || null);
  const [screen, setScreen]           = useState("intro");
  const [qIndex, setQIndex]           = useState(0);
  const [answers, setAnswers]         = useState({});
  const [allTags, setAllTags]         = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [results, setResults]         = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Handle Stripe redirect back
  useEffect(() => {
    const stripeSessionId = searchParams.get("stripe_session_id");
    const qSessionId      = searchParams.get("session_id") || sessionId;
    if (stripeSessionId && qSessionId) {
      (async () => {
        try {
          const res = await axios.get(`${API}/quiz/confirm-payment`, {
            params: { stripe_session_id: stripeSessionId, quiz_session_id: qSessionId }
          });
          if (res.data.status === "paid") {
            const sRes = await axios.get(`${API}/quiz/${qSessionId}/status`);
            setSuccessData({
              sessionId: qSessionId,
              firstName: sRes.data.first_name || "there",
              email:     sRes.data.email || "",
              score:     sRes.data.score,
              ovLow:     sRes.data.overpayment_low,
              ovHigh:    sRes.data.overpayment_high,
            });
            setScreen("success");
          }
        } catch { toast.error(t('quiz.errors.confirm_failed')); }
      })();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeQuestions = questions.filter(q => !q.showIf || q.showIf(answers));

  const handleStart = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quiz/start`);
      const sid = res.data.session_id;
      setSessionId(sid);
      localStorage.setItem("quiz_session_id", sid);
      setScreen("quiz");
    } catch { toast.error(t('quiz.errors.start_failed')); }
    finally { setLoading(false); }
  };

  const handleAnswer = useCallback(async (option, idx) => {
    const currentQ  = activeQuestions[qIndex];
    setSelectedIdx(idx);
    const newAnswers = { ...answers, [currentQ.id]: { text: option.text, tags: option.tags } };
    const newTags    = [...allTags, ...option.tags];
    setAnswers(newAnswers);
    setAllTags(newTags);

    await new Promise(r => setTimeout(r, 280));

    if (qIndex + 1 < activeQuestions.length) {
      setQIndex(qIndex + 1);
      setSelectedIdx(null);
    } else {
      setLoading(true);
      try {
        const answersPayload = {};
        Object.entries(newAnswers).forEach(([qId, ans]) => { answersPayload[qId] = ans; });
        const res = await axios.post(`${API}/quiz/${sessionId}/answers`, {
          answers: answersPayload, all_tags: newTags,
        });
        setResults(res.data);
        setScreen("results");
      } catch { toast.error(t('quiz.errors.save_failed')); }
      finally { setLoading(false); setSelectedIdx(null); }
    }
  }, [qIndex, activeQuestions, answers, allTags, sessionId, t]);

  return (
    <div style={{ minHeight: "100vh", background: "#fafaf9",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div style={{ borderBottom: "1px solid #e7e5e4", padding: "14px 20px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "#fff" }}>
        <span style={{ fontWeight: 800, fontSize: 17, color: "#059669" }}>SolelyTax AI</span>
      </div>

      <div style={{ paddingTop: 24 }}>
        {screen === "intro"   && <IntroScreen onStart={handleStart} loading={loading} />}
        {screen === "quiz"    && activeQuestions[qIndex] && (
          <QuestionScreen
            question={activeQuestions[qIndex]}
            questionNumber={qIndex + 1}
            total={activeQuestions.length}
            onAnswer={handleAnswer}
            selectedIdx={selectedIdx}
          />
        )}
        {screen === "results" && results && (
          <ResultsScreen
            score={results.score} band={results.band}
            gapCount={results.gap_count} strategyCount={results.strategy_count}
            overpaymentDisplay={results.overpayment_display}
            ovLow={results.overpayment_low} ovHigh={results.overpayment_high}
            sessionId={sessionId}
          />
        )}
        {screen === "success" && successData && <SuccessScreen {...successData} />}
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        button:hover:not(:disabled) { opacity: 0.88 !important; }
        input:focus { border-color: #059669 !important; box-shadow: 0 0 0 3px rgba(5,150,105,0.12); }
      `}</style>
    </div>
  );
}
